import { useState, useEffect } from 'react';
import { Download, Loader2, FileBarChart, TrendingUp, TrendingDown, User } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TRY = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(Number(n) || 0);

const DATE_FMT = (d) =>
  new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

const TX_BADGE = { 'Borç': 'rose', 'Tahsilat': 'success', 'Ödeme': 'warning' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl text-xs">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {TRY(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Reports() {
  const { hasPermission } = useAuth();
  const { toast } = useToast();

  const [chartData, setChartData] = useState({ labels: [], borc: [], alacak: [] });
  const [balances, setBalances] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const [isLoadingBalances, setIsLoadingBalances] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Kişi bazlı rapor
  const [cariList, setCariList] = useState([]);
  const [selectedCariId, setSelectedCariId] = useState('');
  const [cariDetail, setCariDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Ana rapor verileri
  useEffect(() => {
    if (!hasPermission('view_reports')) return;

    api.get('/reports/chart-data', { params: { months: 6 } })
      .then(({ data }) => setChartData(data))
      .catch(() => toast({ title: 'Hata', description: 'Grafik verisi yüklenemedi.', variant: 'destructive' }))
      .finally(() => setIsLoadingChart(false));

    api.get('/reports/balances')
      .then(({ data }) => setBalances(data.data))
      .catch(() => {})
      .finally(() => setIsLoadingBalances(false));
  }, []); // eslint-disable-line

  // Cari listesi — kişi bazlı rapor için (bağımsız yüklenir)
  useEffect(() => {
    api.get('/cari', { params: { limit: 500 } })
      .then(({ data }) => setCariList(data.data || []))
      .catch(() => {});
  }, []);

  // Cari seçildiğinde detay yükle
  useEffect(() => {
    if (!selectedCariId) { setCariDetail(null); setDetailError(''); return; }
    setIsLoadingDetail(true);
    setDetailError('');
    setCariDetail(null);
    api.get(`/reports/cari-detail/${selectedCariId}`)
      .then(({ data }) => setCariDetail(data))
      .catch((err) => {
        const msg = err.response?.data?.error || 'Cari detayı yüklenemedi.';
        setDetailError(msg);
        toast({ title: 'Hata', description: msg, variant: 'destructive' });
      })
      .finally(() => setIsLoadingDetail(false));
  }, [selectedCariId]); // eslint-disable-line

  const chartRows = chartData.labels.map((label, i) => ({
    name: label,
    Borç: chartData.borc[i] || 0,
    Alacak: chartData.alacak[i] || 0,
  }));

  const handleExport = async (cariId = null) => {
    setIsExporting(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (cariId) params.cari_id = cariId;

      const response = await api.get('/reports/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = cariId
        ? `${cariDetail?.cari?.name || 'cari'}_${date}.xlsx`
        : `cari_raporu_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'İndirildi', description: 'Excel raporu başarıyla indirildi.', variant: 'success' });
    } catch {
      toast({ title: 'Hata', description: 'Rapor indirilemedi.', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasPermission('view_reports')) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <FileBarChart className="w-12 h-12 mb-3 opacity-30" />
        <p>Bu sayfaya erişim yetkiniz yok.</p>
      </div>
    );
  }

  const totals = balances.reduce(
    (acc, row) => {
      const b = parseFloat(row.current_balance) || 0;
      if (b > 0) acc.alacak += b;
      else acc.borc += Math.abs(b);
      return acc;
    },
    { alacak: 0, borc: 0 }
  );

  // Kişi bazlı özet
  const detailTotals = cariDetail?.transactions?.reduce(
    (acc, tx) => {
      const tryVal = parseFloat(tx.amount) * (parseFloat(tx.exchange_rate) || 1);
      if (tx.type === 'Borç') acc.borc += tryVal;
      else if (tx.type === 'Tahsilat' || tx.type === 'Ödeme') acc.tahsilat += tryVal;
      return acc;
    },
    { borc: 0, tahsilat: 0 }
  ) || { borc: 0, tahsilat: 0 };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Raporlar</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Finansal performans görselleştirmesi</p>
        </div>
        {hasPermission('export_excel') && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
              <span className="text-slate-600 text-sm">—</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
            </div>
            <Button onClick={() => handleExport(null)} disabled={isExporting} className="gap-2">
              {isExporting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> İndiriliyor...</>
                : <><Download className="w-4 h-4" /> Tüm Raporu İndir</>}
            </Button>
          </div>
        )}
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <CardTitle className="text-sm">Aylık Bakiye Trendi</CardTitle>
            </div>
            <CardDescription className="text-xs">Son 6 aylık borç ve alacak karşılaştırması</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingChart ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              </div>
            ) : chartRows.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">Henüz veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartRows} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorAlacak" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorBorc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={v => (v / 1000).toFixed(0) + 'K'} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                  <Area type="monotone" dataKey="Alacak" stroke="#10b981" strokeWidth={2} fill="url(#colorAlacak)" dot={false} />
                  <Area type="monotone" dataKey="Borç" stroke="#f43f5e" strokeWidth={2} fill="url(#colorBorc)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-rose-400" />
              <CardTitle className="text-sm">Borç / Alacak Karşılaştırması</CardTitle>
            </div>
            <CardDescription className="text-xs">Aylık grup bazında karşılaştırma</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingChart ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              </div>
            ) : chartRows.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">Henüz veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartRows} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={v => (v / 1000).toFixed(0) + 'K'} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                  <Bar dataKey="Alacak" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={30} />
                  <Bar dataKey="Borç" fill="#f43f5e" radius={[3, 3, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cari Bakiye Tablosu */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Cari Bakiye Raporu</CardTitle>
              <CardDescription>{balances.length} cari kaydı</CardDescription>
            </div>
            <div className="flex gap-3 text-xs text-slate-400">
              <span className="text-emerald-400">Alacak: {TRY(totals.alacak)}</span>
              <span className="text-rose-400">Borç: {TRY(totals.borc)}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoadingBalances ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            </div>
          ) : balances.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-slate-500 text-sm">
              Henüz cari kaydı yok
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cari Adı</TableHead>
                  <TableHead className="text-right">Açılış Bakiyesi</TableHead>
                  <TableHead className="text-right">İşlem Toplamı</TableHead>
                  <TableHead className="text-right">Güncel Bakiye</TableHead>
                  <TableHead>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.map((row) => {
                  const bal = parseFloat(row.current_balance);
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium text-slate-900 dark:text-slate-100">{row.name}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500 dark:text-slate-400 text-xs">
                        {TRY(row.opening_balance)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-500 dark:text-slate-400 text-xs">
                        {TRY(row.txn_balance)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${bal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {TRY(bal)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={bal >= 0 ? 'success' : 'rose'}>
                          {bal >= 0 ? 'Alacak' : 'Borç'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Kişi Bazlı Rapor ────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-blue-400" />
            <CardTitle className="text-base">Kişi Bazlı Rapor</CardTitle>
          </div>
          <CardDescription>Seçilen carinin tüm işlem geçmişini görüntüleyin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cari seçici */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-52">
              <Label className="text-xs mb-1 block">Cari Seçin</Label>
              <Select value={selectedCariId} onValueChange={setSelectedCariId}>
                <SelectTrigger><SelectValue placeholder="Cari seçin..." /></SelectTrigger>
                <SelectContent>
                  {cariList.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedCariId && hasPermission('export_excel') && (
              <Button variant="outline" size="sm" className="gap-2"
                onClick={() => handleExport(selectedCariId)} disabled={isExporting}>
                <Download className="w-3.5 h-3.5" /> Excel İndir
              </Button>
            )}
          </div>

          {/* Detay içeriği */}
          {!selectedCariId ? (
            <div className="flex items-center justify-center h-20 text-slate-500 text-sm">
              Yukarıdan bir cari seçin
            </div>
          ) : isLoadingDetail ? (
            <div className="flex items-center justify-center h-20">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            </div>
          ) : detailError ? (
            <div className="flex items-center justify-center h-20 text-rose-400 text-sm">
              {detailError}
            </div>
          ) : cariDetail ? (
            <>
              {/* Özet kartları */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Cari Adı', value: cariDetail.cari.name, color: 'text-slate-900 dark:text-slate-100' },
                  { label: 'Güncel Bakiye', value: TRY(cariDetail.cari.current_balance), color: parseFloat(cariDetail.cari.current_balance) >= 0 ? 'text-emerald-400' : 'text-rose-400' },
                  { label: 'Toplam Borç', value: TRY(detailTotals.borc), color: 'text-rose-400' },
                  { label: 'Toplam Tahsilat', value: TRY(detailTotals.tahsilat), color: 'text-emerald-400' },
                ].map(s => (
                  <div key={s.label} className="rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/50 p-3">
                    <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                    <p className={`font-semibold text-sm truncate ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* İşlem tablosu */}
              {cariDetail.transactions.length === 0 ? (
                <div className="flex items-center justify-center h-20 text-slate-500 text-sm">
                  Bu cariye ait işlem yok
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                        <TableHead className="text-right">TRY Karşılığı</TableHead>
                        <TableHead>Açıklama</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cariDetail.transactions.map((tx) => {
                        const rate = parseFloat(tx.exchange_rate) || 1;
                        const sym = CURRENCY_SYMBOLS[tx.currency] || '₺';
                        const tryVal = parseFloat(tx.amount) * rate;
                        return (
                          <TableRow key={tx.id}>
                            <TableCell className="text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                              {DATE_FMT(tx.transaction_date)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={TX_BADGE[tx.type] || 'secondary'}>{tx.type}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-slate-800 dark:text-slate-200">
                              {sym}{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(tx.amount)}
                              {tx.currency !== 'TRY' && (
                                <span className="block text-xs text-slate-500 font-normal">kur: {rate}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-700 dark:text-slate-300 text-sm">
                              {TRY(tryVal)}
                            </TableCell>
                            <TableCell className="text-slate-500 dark:text-slate-400 text-xs max-w-36 truncate">
                              {tx.description || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
